import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { fetchInventoryItems } from '@/renderer/services/inventoryItemService';
import type {
  InventoryItemFilterParams,
  InventoryItemRecord,
} from '@/renderer/types/inventory';
import { useInventoryItems } from './useInventoryItems';

const DEFAULT_PAGE_SIZE = 20;

const buildItemLabel = (item: InventoryItemRecord) =>
  item.sku ? `${item.name} (${item.sku})` : item.name;

const dedupeItems = (items: InventoryItemRecord[]) =>
  Array.from(new Map(items.map((item) => [item.id, item])).values());

interface UseInventoryItemSelectOptionsParams {
  companyId: number | null | undefined;
  status?: InventoryItemFilterParams['status'];
  itemIds?: string[];
  selectedItemIds?: string[];
  pageSize?: number;
}

export function useInventoryItemSelectOptions({
  companyId,
  status = 'all',
  itemIds,
  selectedItemIds,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseInventoryItemSelectOptionsParams) {
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [page, setPage] = useState(1);
  const [loadedItems, setLoadedItems] = useState<InventoryItemRecord[]>([]);

  const normalizedItemIds = useMemo(
    () => Array.from(new Set((itemIds ?? []).filter(Boolean))),
    [itemIds],
  );
  const normalizedSelectedItemIds = useMemo(
    () => Array.from(new Set((selectedItemIds ?? []).filter(Boolean))),
    [selectedItemIds],
  );

  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page,
    pageSize,
    search: deferredSearchQuery.trim(),
    categoryId: 'all',
    status,
    itemIds: normalizedItemIds,
  });

  const selectedItemsQuery = useQuery({
    queryKey: [
      'inventory',
      'item-select-selected-items',
      companyId ?? 0,
      normalizedSelectedItemIds,
    ],
    queryFn: async () => {
      const result = await fetchInventoryItems({
        companyId: companyId ?? 0,
        page: 1,
        pageSize: Math.max(normalizedSelectedItemIds.length, 1),
        search: '',
        categoryId: 'all',
        status: 'all',
        itemIds: normalizedSelectedItemIds,
      });

      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to fetch selected items');
      }

      return result.data.data;
    },
    enabled: !!companyId && normalizedSelectedItemIds.length > 0,
  });

  useEffect(() => {
    setPage(1);
    setLoadedItems([]);
  }, [companyId, status, deferredSearchQuery, normalizedItemIds]);

  useEffect(() => {
    const nextItems = itemsQuery.data?.data;
    if (!nextItems) return;

    setLoadedItems((previous) =>
      dedupeItems(page === 1 ? nextItems : [...previous, ...nextItems]),
    );
  }, [itemsQuery.data?.data, page]);

  // `items` includes selections from every line in the form (fetched via
  // selectedItemsQuery) so labels/units can be resolved for items that
  // aren't on the currently-loaded page. `options` — what the dropdown
  // actually renders — must come from `loadedItems` alone, otherwise a
  // search in one line leaks other lines' selections into its results.
  const items = useMemo(
    () => dedupeItems([...(selectedItemsQuery.data ?? []), ...loadedItems]),
    [loadedItems, selectedItemsQuery.data],
  );

  const options = useMemo(
    () =>
      loadedItems.map((item) => ({
        value: item.id,
        label: buildItemLabel(item),
      })),
    [loadedItems],
  );

  return {
    items,
    options,
    isLoading: itemsQuery.isFetching || selectedItemsQuery.isFetching,
    hasMore: Boolean(itemsQuery.data?.pagination.hasNextPage),
    setSearchQuery,
    loadMore: () => {
      if (itemsQuery.data?.pagination.hasNextPage) {
        setPage((current) => current + 1);
      }
    },
    getItemLabel: (itemId: string) => {
      const item = items.find((candidate) => candidate.id === itemId);
      return item ? buildItemLabel(item) : undefined;
    },
  };
}
